import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Evaluation, Question, UserAnswer } from '@/types';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import { router } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface QuestionData {
    question: Question;
    user_answer: UserAnswer | null;
}

interface ReviewProps {
    evaluation: Evaluation;
    questions_data: QuestionData[];
    course: {
        slug: string;
    };
}

const ReviewContent: React.FC<ReviewProps> = ({
    evaluation,
    questions_data,
    course,
}) => {
    const { t } = useTranslation();
    const pageTitle = `Revisar - ${evaluation.title}`;

    const getSelectedAnswerIds = (questionData: QuestionData) => {
        if (!questionData.user_answer) return [];
        const selectedIds = questionData.user_answer.selected_answer_ids || [];
        if (selectedIds.length > 0) return selectedIds;
        return questionData.user_answer.selected_answer_id
            ? [questionData.user_answer.selected_answer_id]
            : [];
    };

    const answeredCount = questions_data.filter((qd) => getSelectedAnswerIds(qd).length > 0).length;
    const unansweredCount = questions_data.length - answeredCount;

    const getAnswerText = (questionData: QuestionData) => {
        const selectedAnswerIds = getSelectedAnswerIds(questionData);
        if (selectedAnswerIds.length === 0) {
            return t('evaluation.review.notAnswered');
        }

        const answers =
            questionData.question.answers?.filter((answer) =>
                selectedAnswerIds.includes(answer.id)
            ) || [];

        return answers.length > 0
            ? answers.map((answer) => answer.answer).join(', ')
            : t('evaluation.review.notAnswered');
    };

    const getAnswerLetter = (questionData: QuestionData) => {
        const selectedAnswerIds = getSelectedAnswerIds(questionData);
        if (selectedAnswerIds.length === 0) return '-';

        const letters = selectedAnswerIds
            .map((selectedId) =>
                questionData.question.answers?.findIndex(
                    (answer) => answer.id === selectedId
                )
            )
            .filter((index): index is number => index !== undefined && index >= 0)
            .map((index) => String.fromCharCode(65 + index));

        return letters.length > 0 ? letters.join(', ') : '-';
    };

    const handleSubmit = () => {
        router.post(`/evaluations/${evaluation.id}/review/`);
    };

    const handleBack = () => {
        // Go back to last question
        const lastQuestionIndex = questions_data.length - 1;
        router.get(
            `/evaluations/${evaluation.id}/questions/${lastQuestionIndex}/`
        );
    };

    return (
        <AppLayout>
            <PageTitle title={pageTitle} />
            <div className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="border-b border-gray-200 bg-white shadow-sm">
                    <div className="mx-auto max-w-4xl px-4 py-6">
                        <h1 className="mb-2 text-2xl font-bold text-gray-800">
                            {t('evaluation.review.title')}
                        </h1>
                        <p className="text-gray-600">{evaluation.title}</p>
                    </div>
                </div>

                <div className="mx-auto max-w-4xl px-4 py-8">
                    {/* Summary Card */}
                    <div className="mb-8 rounded-lg bg-white p-6 shadow-lg">
                        <h2 className="mb-4 text-xl font-semibold text-gray-800">
                            {t('evaluation.review.summary')}
                        </h2>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                            <div className="text-center">
                                <div className="text-cb-primary mb-1 text-3xl font-bold">
                                    {answeredCount}
                                </div>
                                <p className="text-sm text-gray-600">
                                    {t('evaluation.review.answered')}
                                </p>
                            </div>

                            <div className="text-center">
                                <div
                                    className={`mb-1 text-3xl font-bold ${
                                        unansweredCount > 0
                                            ? 'text-red-600'
                                            : 'text-gray-400'
                                    }`}
                                >
                                    {unansweredCount}
                                </div>
                                <p className="text-sm text-gray-600">
                                    {t('evaluation.review.unanswered')}
                                </p>
                            </div>

                            <div className="text-center">
                                <div className="mb-1 text-3xl font-bold text-gray-800">
                                    {questions_data.length}
                                </div>
                                <p className="text-sm text-gray-600">
                                    {t('evaluation.review.total')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Questions Review */}
                    <div className="mb-8 overflow-hidden rounded-lg bg-white shadow-lg">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-semibold text-gray-800">
                                {t('evaluation.review.yourAnswers')}
                            </h2>
                        </div>

                        <div className="divide-y divide-gray-200">
                            {questions_data.map((questionData, index) => {
                                const isAnswered = questionData.user_answer;

                                return (
                                    <div
                                        key={questionData.question.id}
                                        className="px-6 py-4"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div
                                                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-medium ${
                                                    isAnswered
                                                        ? 'bg-cb-light text-cb-text'
                                                        : 'bg-gray-100 text-gray-500'
                                                }`}
                                            >
                                                {index + 1}
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <h3
                                                    className="mb-2 text-sm leading-relaxed font-medium text-gray-800"
                                                    dangerouslySetInnerHTML={{
                                                        __html: cleanWordPressHtml(
                                                            questionData
                                                                .question
                                                                .question
                                                        ),
                                                    }}
                                                />

                                                <div className="flex items-start gap-3">
                                                    <div
                                                        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                                                            isAnswered
                                                                ? 'bg-cb-primary text-white'
                                                                : 'bg-gray-300 text-gray-600'
                                                        }`}
                                                    >
                                                        {getAnswerLetter(
                                                            questionData
                                                        )}
                                                    </div>

                                                    <div
                                                        className={`flex-1 text-sm ${
                                                            isAnswered
                                                                ? 'text-gray-700'
                                                                : 'text-gray-500 italic'
                                                        }`}
                                                    >
                                                        {getAnswerText(
                                                            questionData
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col justify-center gap-4 sm:flex-row">
                        <SecondaryButton onClick={handleBack} size="md">
                            {t('evaluation.review.backToQuestions')}
                        </SecondaryButton>

                        <PrimaryButton onClick={handleSubmit} size="md">
                            {t('evaluation.review.submitEvaluation')}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const Review: React.FC<ReviewProps> = (props) => {
    return (
        <ThemeWrapper>
            <ReviewContent {...props} />
        </ThemeWrapper>
    );
};

export default Review;
